import { useTheme } from '@emotion/react'
import {
    BreachEvent,
    click_origin_breach_screen,
    DataLayerEventName,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import React, { useEffect } from 'react'
import { getAttribution } from '../../../../../../errors/ErrorPage'
import { useAuthActions } from '../../../../../authentication/AuthenticationProvider'
import { getSubscriptionValue, SubscriptionPackages } from '../../../../common'
import { createLinkClickEvent } from '../../../../createLinkClickHandler'
import SubscriptionPackage from '../../../SubscriptionPackage/SubscriptionPackage'
import { WestRewardsMessage } from '../../shared-components/WestRewardsMessage/WestRewardsMessage'
import {
    StyledBreachWallContent,
    StyledButton,
    StyledHelpMessage,
    StyledPackage,
    StyledRegistrationInfo,
    StyledTextLink,
} from './BreachWallContent.styled'

export interface BreachWallContentProps {
    title: () => JSX.Element | null
    terms: () => JSX.Element | null
    mediaBanner: () => JSX.Element | null
    subscriptionPackages?: SubscriptionPackages

    breachCreativeName: string
    breachDeal: string

    onEvent: (event: BreachEvent) => void
}

export const BreachWallContent: React.FC<BreachWallContentProps> = ({
    title,
    subscriptionPackages,
    breachCreativeName,
    breachDeal,
    onEvent,
    mediaBanner,
    terms,
}) => {
    const renderMediaBanner = mediaBanner
    const renderTitleSection = title
    const renderTerms = terms
    const { onSubscribeClick } = useAuthActions()
    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'

    useEffect(() => {
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            subscriptionPackages?.map((pkg, index) => ({
                index,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                price: getSubscriptionValue(pkg.weeklyPrice),
                quantity: 1,
                currency: 'AUD',
            })) || []

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <StyledBreachWallContent>
            {renderMediaBanner()}
            <StyledRegistrationInfo>
                {renderTitleSection()}
            </StyledRegistrationInfo>
            <StyledPackage>
                {subscriptionPackages &&
                    subscriptionPackages.map(
                        (subscriptionPackageItem, index) => (
                            <SubscriptionPackage
                                key={index}
                                breachCreativeName={breachCreativeName}
                                breachDeal={breachDeal}
                                onEvent={onEvent}
                                packageInfo={subscriptionPackageItem}
                                masthead={masthead}
                            />
                        ),
                    )}
            </StyledPackage>
            <StyledButton
                onClick={() => {
                    createLinkClickEvent(
                        breachCreativeName,
                        breachDeal,
                        onEvent,
                    )()
                    onSubscribeClick({
                        callToAction: click_origin_breach_screen,
                    })
                }}
            >
                View All Packages
            </StyledButton>
            <StyledHelpMessage>
                Need Help? Call us at{' '}
                <StyledTextLink reverseColor={true} href="tel:1800 811 855">
                    1800 811 855
                </StyledTextLink>
            </StyledHelpMessage>
            <WestRewardsMessage />
            {renderTerms()}
        </StyledBreachWallContent>
    )
}
BreachWallContent.displayName = 'BreachWallContent'

export default BreachWallContent
